import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, User, Shield, ShieldCheck, Pencil, X, Save, Eye, EyeOff, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    name: '',
    password: '',
    role: 'vendedor'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingUser && (!formData.username || !formData.email || !formData.password || !formData.name)) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    
    try {
      if (editingUser) {
        // Update existing user
        const updateData = {};
        if (formData.name !== editingUser.name) updateData.name = formData.name;
        if (formData.username !== editingUser.username) updateData.username = formData.username;
        if (formData.email !== editingUser.email) updateData.email = formData.email;
        if (formData.role !== editingUser.role) updateData.role = formData.role;
        if (formData.password) updateData.password = formData.password;
        
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success('Usuario actualizado exitosamente');
      } else {
        // Create new user
        await axios.post(`${API}/auth/register`, formData);
        toast.success('Usuario creado exitosamente');
      }
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username || '',
      email: user.email,
      name: user.name,
      password: '',
      role: user.role
    });
    setShowModal(true);
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role?role=${newRole}`);
      toast.success('Rol actualizado');
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar rol');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      name: '',
      password: '',
      role: 'vendedor'
    });
    setEditingUser(null);
    setShowPassword(false);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]" />
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-3xl font-black text-white">Usuarios</h1>
            <p className="text-zinc-500 text-sm">{users.length} usuarios registrados</p>
          </div>
          <Button
            onClick={fetchUsers}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
        <Button
          data-testid="add-user-btn"
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold uppercase tracking-wider rounded-sm primary-glow"
        >
          <Plus size={18} className="mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((user) => (
          <Card
            key={user.id}
            data-testid={`user-${user.id}`}
            className="bg-[#0A0A0A] border-zinc-800 hover:border-zinc-700 transition-colors"
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                    user.role === 'admin' ? 'bg-[#FF3B30]/20' : 'bg-zinc-800'
                  }`}>
                    {user.role === 'admin' 
                      ? <ShieldCheck className="text-[#FF3B30]" size={24} />
                      : <User className="text-zinc-500" size={24} />
                    }
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{user.name}</h3>
                    <p className="text-sm text-zinc-500">@{user.username}</p>
                    <p className="text-xs text-zinc-600">{user.email}</p>
                  </div>
                </div>
                {currentUser?.id !== user.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(user)}
                    className="text-zinc-400 hover:text-white hover:bg-zinc-800"
                  >
                    <Pencil size={16} />
                  </Button>
                )}
              </div>

              <div className="flex items-center justify-between mb-4">
                <Badge 
                  variant="outline"
                  className={user.role === 'admin' 
                    ? 'border-[#FF3B30] text-[#FF3B30]' 
                    : 'border-zinc-600 text-zinc-400'
                  }
                >
                  {user.role === 'admin' ? 'Administrador' : 'Vendedor'}
                </Badge>
                <span className="text-xs text-zinc-600">
                  Desde {formatDate(user.created_at)}
                </span>
              </div>

              {currentUser?.id !== user.id && (
                <div className="flex gap-2 pt-4 border-t border-zinc-800">
                  <Select 
                    value={user.role} 
                    onValueChange={(v) => handleRoleChange(user.id, v)}
                  >
                    <SelectTrigger className="flex-1 bg-zinc-900 border-zinc-800 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-zinc-800">
                      <SelectItem value="admin">
                        <span className="flex items-center gap-2">
                          <ShieldCheck size={14} /> Administrador
                        </span>
                      </SelectItem>
                      <SelectItem value="vendedor">
                        <span className="flex items-center gap-2">
                          <User size={14} /> Vendedor
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(user.id)}
                    className="border-red-900/50 text-red-400 hover:bg-red-950 hover:text-red-300"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); setShowModal(open); }}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {editingUser ? <Pencil size={20} /> : <Plus size={20} />}
              {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Nombre Completo</Label>
              <Input
                data-testid="user-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-zinc-900 border-zinc-800 focus:border-[#FF3B30] text-white"
                placeholder="Juan Pérez"
                required={!editingUser}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Usuario</Label>
              <Input
                data-testid="user-username-input"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                className="bg-zinc-900 border-zinc-800 focus:border-[#FF3B30] text-white"
                placeholder="juan_perez"
                required={!editingUser}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Email</Label>
              <Input
                data-testid="user-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-zinc-900 border-zinc-800 focus:border-[#FF3B30] text-white"
                placeholder="correo@ejemplo.com"
                required={!editingUser}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">
                {editingUser ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña'}
              </Label>
              <div className="relative">
                <Input
                  data-testid="user-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 focus:border-[#FF3B30] text-white pr-10"
                  placeholder={editingUser ? '••••••••' : 'Mínimo 6 caracteres'}
                  required={!editingUser}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Rol</Label>
              <Select
                value={formData.role}
                onValueChange={(v) => setFormData({ ...formData, role: v })}
              >
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-zinc-800">
                  <SelectItem value="vendedor">Vendedor</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => { resetForm(); setShowModal(false); }}
                className="border-zinc-700"
              >
                <X size={16} className="mr-2" />
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#FF3B30] hover:bg-[#D32F2F] text-white"
              >
                <Save size={16} className="mr-2" />
                {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
